<template>
  <v-row>
    <v-btn-toggle group>
      <v-btn text router :to="`/customerdetails/${id}/settings`">Settings</v-btn>
      <v-btn text router :to="`/customerdetails/${id}/licenses`">Licenses</v-btn>

      <!-- Module Config dropdown - visible for SUPER_ADMIN and ADMIN -->
      <v-menu v-if="canAccessModuleConfig && availableModuleConfigs.length > 0" offset-y>
        <template v-slot:activator="{ on }">
          <v-btn text v-on="on">
            Module Config
            <v-icon right>mdi-menu-down</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item
            v-for="config in availableModuleConfigs"
            :key="`${config.appCode}-${config.moduleName}`"
          >
            <v-btn :to="`/customerdetails/${id}/module-config/${config.appCode}/${config.moduleName}`" text>
              {{ config.label }}
            </v-btn>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn text v-on="on">
            Users
            <v-icon right>mdi-menu-down</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item>
            <v-btn :to="`/customerdetails/${id}/admins`" text>
              Admins
            </v-btn>
          </v-list-item>
          <v-list-item>
            <v-btn :to="`/customerdetails/${id}/users`" text>
              Users
            </v-btn>
          </v-list-item>
          <v-list-item>
            <v-btn :to="`/customerdetails/${id}/integration`" text>
              Integration
            </v-btn>
          </v-list-item>
          <v-list-item>
            <v-btn :to="`/customerdetails/${id}/endcustomer`" text>
              End customers
            </v-btn>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-btn-toggle>
  </v-row>
</template>

<script>
export default {
  name: 'CustomerDetailsMenuLinks',

  data() {
    return {
      customerAppCodes: [],
      customerLicensedModules: [], // Store actual licensed modules
      isLoadingLicenses: false,
      supportedModuleConfigs: [
        { label: 'Lieferschein Core', appCode: 'DDN', moduleName: 'core' },
        { label: 'FoodOrder Core', appCode: 'KOA', moduleName: 'core' },
        { label: 'FoodOrder AI Order Import', appCode: 'KOA', moduleName: 'ai order import' },
      ],
    };
  },

  computed: {
    id() {
      return this.$route.params.id;
    },
    isSuperAdmin() {
      return this.$store.getters['auth/isSuperuser'];
    },
    isAdmin() {
      return this.$store.state.auth.permissions.includes('ROLE_ADMIN');
    },
    canAccessModuleConfig() {
      return this.isSuperAdmin || this.isAdmin;
    },
    availableModuleConfigs() {
      // Filter by actual licensed modules, not just app codes
      const filtered = this.supportedModuleConfigs.filter(config => this.customerLicensedModules.some(
        licensed => licensed.appCode === config.appCode && licensed.moduleName === config.moduleName,
      ));
      return filtered.sort((a, b) => {
        if (a.appCode !== b.appCode) {
          return a.appCode.localeCompare(b.appCode);
        }
        if (a.moduleName === 'core' && b.moduleName !== 'core') return -1;
        if (a.moduleName !== 'core' && b.moduleName === 'core') return 1;
        return a.moduleName.localeCompare(b.moduleName);
      });
    },
  },

  watch: {
    id: {
      immediate: true,
      handler(newId) {
        if (newId && this.canAccessModuleConfig) {
          this.loadCustomerLicenses();
        }
      },
    },
    // Watch for license updates from Vuex store
    '$store.state.licensesUpdatedTimestamp': function watchLicenseUpdates() {
      if (this.id && this.canAccessModuleConfig) {
        this.loadCustomerLicenses();
      }
    },
  },

  methods: {
    async loadCustomerLicenses() {
      if (this.isLoadingLicenses) return;
      this.isLoadingLicenses = true;

      try {
        const response = await this.$http.get('/api/licences', {
          params: {
            'customer.id': this.id,
            itemsPerPage: 100,
          },
          headers: {
            Accept: 'application/ld+json',
          },
        });

        const licenses = response.data['hydra:member'] || [];
        const appCodes = new Set();
        const licensedModules = [];

        licenses.forEach((license) => {
          if (license.app && license.app.code) {
            appCodes.add(license.app.code);

            // Extract all modules from this license
            if (license.modules && Array.isArray(license.modules)) {
              license.modules.forEach((module) => {
                licensedModules.push({
                  appCode: license.app.code,
                  moduleName: module.name,
                });
              });
            }
          }
        });

        this.customerAppCodes = Array.from(appCodes);
        this.customerLicensedModules = licensedModules;
      } catch (error) {
        this.customerAppCodes = [];
        this.customerLicensedModules = [];
      } finally {
        this.isLoadingLicenses = false;
      }
    },
  },
};
</script>


<style scoped lang="scss">
  .navbar-wrapper {

    nav.navbar-expand-lg {
      ul {
        position: relative;
        left: -25px;

        li {
          a {
            position: relative;
            color: #ffffff;
            font-weight: 300;
            font-size: 20px;
            letter-spacing: .0015em;
            padding-left: 25px;
            padding-right: 25px;

            &.router-link-exact-active {
              &:after {
                content: '';
                position: absolute;
                width: 50%;
                bottom: 0;
                left: 0;
                margin-left: 25%;
                height: 3px;
                background-color: #ADCE6D;
              }
            }
          }
        }
      }
    }
  }
</style>
